import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { menuService } from '../utils/api';

/**
 * Custom hook for fetching menu items with caching
 */
export const useMenuItems = (menuId, dayId) => {
  return useQuery({
    queryKey: ['menuItems', menuId, dayId],
    queryFn: () => menuService.getMenuItems(menuId, dayId),
    enabled: !!menuId && !!dayId,
  });
};

/**
 * Custom hook for fetching menu items for a specific day
 */
export const useMenuItemsForDay = (dayId) => {
  return useQuery({
    queryKey: ['menuItemsForDay', dayId],
    queryFn: () => menuService.getMenuItemsForDay(dayId),
    enabled: !!dayId,
  });
};

/**
 * Custom hook for saving a menu item with optimistic updates
 */
export const useSaveMenuItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => menuService.saveMenuItem(data),
    onSuccess: (data, variables) => {
      // Invalidate relevant queries to ensure data is fresh
      if (variables.dayId) {
        queryClient.invalidateQueries(['menuItemsForDay', variables.dayId]);
      }
      
      // Also invalidate any broader queries that might include this item
      queryClient.invalidateQueries(['menu']);
    },
  });
};

/**
 * Custom hook for deleting a menu item with optimistic updates
 */
export const useDeleteMenuItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dayId, courseId }) => menuService.deleteMenuItem(dayId, courseId),
    onMutate: async ({ dayId, courseId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries(['menuItemsForDay', dayId]);

      // Snapshot the previous value
      const previousMenuItems = queryClient.getQueryData(['menuItemsForDay', dayId]);

      // Optimistically update to the new value
      if (previousMenuItems) {
        queryClient.setQueryData(['menuItemsForDay', dayId], 
          previousMenuItems.filter(item => item.course_id !== courseId)
        );
      }

      return { previousMenuItems };
    },
    onError: (err, { dayId }, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousMenuItems) {
        queryClient.setQueryData(['menuItemsForDay', dayId], context.previousMenuItems);
      }
    },
    onSettled: (data, error, { dayId }) => {
      // Always refetch after error or success to ensure data consistency
      queryClient.invalidateQueries(['menuItemsForDay', dayId]);
      // Also invalidate any broader queries that might include this item
      queryClient.invalidateQueries(['menu']);
    },
  });
};

/**
 * Custom hook for updating menu items with optimistic updates
 */
export const useUpdateMenuItems = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ menuId, dayId, items }) => 
      menuService.updateMenuItems(menuId, dayId, items),
    
    onMutate: async ({ menuId, dayId, items }) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey: ['menuItems', menuId, dayId],
      });
      
      // Also cancel menu detail queries that would contain these items
      await queryClient.cancelQueries({
        queryKey: ['menuDetails', menuId],
      });

      // Snapshot the previous value
      const previousItems = queryClient.getQueryData(['menuItems', menuId, dayId]);
      
      // Optimistically update the cache with new items
      queryClient.setQueryData(['menuItems', menuId, dayId], items);

      // If we have the menu details cached, update that too
      const menuDetails = queryClient.getQueryData(['menuDetails', menuId]);
      if (menuDetails) {
        const dayIndex = menuDetails.days.findIndex(d => d.id === dayId);
        if (dayIndex >= 0) {
          const updatedDays = [...menuDetails.days];
          updatedDays[dayIndex] = {
            ...updatedDays[dayIndex],
            items: items,
          };
          
          queryClient.setQueryData(
            ['menuDetails', menuId], 
            { ...menuDetails, days: updatedDays }
          );
        }
      }

      // Return a context object with the previous values
      return { previousItems, menuDetails };
    },
    
    onError: (err, { menuId, dayId }, context) => {
      // If the mutation fails, revert the cache to the previous value
      if (context?.previousItems) {
        queryClient.setQueryData(
          ['menuItems', menuId, dayId], 
          context.previousItems
        );
      }
      
      // If we modified the menu details, revert those too
      if (context?.menuDetails) {
        queryClient.setQueryData(
          ['menuDetails', menuId], 
          context.menuDetails
        );
      }
    },
    
    onSettled: (data, error, { menuId, dayId }) => {
      // Regardless of whether the mutation succeeded or failed,
      // refetch both queries to ensure the local data is in sync with the server
      queryClient.invalidateQueries({
        queryKey: ['menuItems', menuId, dayId],
      });
      queryClient.invalidateQueries({
        queryKey: ['menuDetails', menuId],
      });
      
      // Also invalidate public menu data that might be affected
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'publicMenu'
      });
      
      // Also invalidate available menu types cache
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'availableMenuTypes'
      });
    },
  });
};

/**
 * Custom hook for setting a menu item's dish with optimistic updates
 */
export const useSetMenuItemDish = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ menuId, dayId, itemId, dishId }) => 
      menuService.setMenuItemDish(menuId, dayId, itemId, dishId),
    
    onMutate: async ({ menuId, dayId, itemId, dishId, dishName }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['menuItems', menuId, dayId],
      });

      // Snapshot the previous values
      const previousItems = queryClient.getQueryData(['menuItems', menuId, dayId]);
      
      // Optimistically update the cache
      if (previousItems) {
        const updatedItems = previousItems.map(item => 
          item.id === itemId 
            ? { 
                ...item, 
                dish_id: dishId,
                dish_name: dishName || 'Loading...' // Optimistic name if provided
              } 
            : item
        );
        
        queryClient.setQueryData(['menuItems', menuId, dayId], updatedItems);
      }

      // Return a context with the previous values
      return { previousItems };
    },
    
    onError: (err, { menuId, dayId }, context) => {
      // If the mutation fails, revert the cache
      if (context?.previousItems) {
        queryClient.setQueryData(
          ['menuItems', menuId, dayId], 
          context.previousItems
        );
      }
    },
    
    onSettled: (data, error, { menuId, dayId }) => {
      // Refetch to ensure the local data is in sync with the server
      queryClient.invalidateQueries({
        queryKey: ['menuItems', menuId, dayId],
      });
      queryClient.invalidateQueries({
        queryKey: ['menuDetails', menuId],
      });
      
      // Also invalidate public menu data that might be affected
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'publicMenu'
      });
    },
  });
};

/**
 * Custom hook for clearing a menu item's dish with optimistic updates
 */
export const useClearMenuItemDish = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ menuId, dayId, itemId }) => 
      menuService.clearMenuItemDish(menuId, dayId, itemId),
    
    onMutate: async ({ menuId, dayId, itemId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['menuItems', menuId, dayId],
      });

      // Snapshot the previous values
      const previousItems = queryClient.getQueryData(['menuItems', menuId, dayId]);
      
      // Optimistically update the cache
      if (previousItems) {
        const updatedItems = previousItems.map(item => 
          item.id === itemId 
            ? { 
                ...item, 
                dish_id: null,
                dish_name: null 
              } 
            : item
        );
        
        queryClient.setQueryData(['menuItems', menuId, dayId], updatedItems);
      }

      // Return a context with the previous values
      return { previousItems };
    },
    
    onError: (err, { menuId, dayId }, context) => {
      // If the mutation fails, revert the cache
      if (context?.previousItems) {
        queryClient.setQueryData(
          ['menuItems', menuId, dayId], 
          context.previousItems
        );
      }
    },
    
    onSettled: (data, error, { menuId, dayId }) => {
      // Refetch to ensure the local data is in sync with the server
      queryClient.invalidateQueries({
        queryKey: ['menuItems', menuId, dayId],
      });
      queryClient.invalidateQueries({
        queryKey: ['menuDetails', menuId],
      });
      
      // Also invalidate public menu data that might be affected
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'publicMenu'
      });
    },
  });
};

export const useUpdateMenuItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    (updatedItem) => menuService.saveMenuItem(updatedItem),
    {
      // When mutate is called:
      onMutate: async (updatedItem) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries(['menuItems']);
        
        // Snapshot the previous value
        const previousMenuItems = queryClient.getQueryData(['menuItems']);
        
        // Optimistically update to the new value
        queryClient.setQueryData(['menuItems'], old => {
          if (!old) return [updatedItem];
          return old.map(item => 
            item.id === updatedItem.id ? updatedItem : item
          );
        });
        
        // Return a context object with the snapshotted value
        return { previousMenuItems };
      },
      // If the mutation fails, use the context returned from onMutate to roll back
      onError: (err, updatedItem, context) => {
        if (context?.previousMenuItems) {
          queryClient.setQueryData(['menuItems'], context.previousMenuItems);
        }
      },
      // Always refetch after error or success:
      onSettled: () => {
        queryClient.invalidateQueries(['menuItems']);
        
        // Also invalidate public menu data that might be affected
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === 'publicMenu'
        });
      },
    }
  );
}; 